import React, { useState, useEffect, useRef } from 'react';

import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import { uploadRestaurantLogo } from '../../../services/storageService';

const RestaurantProfileCard = ({ profile, onUpdate, restaurantId, onError }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState(profile);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!isEditing) {
      setFormData(profile);
    }
  }, [profile, isEditing]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    onUpdate({
      ...formData,
      restaurantId: restaurantId || formData?.restaurantId || formData?.restaurant_id,
      restaurant_id: restaurantId || formData?.restaurantId || formData?.restaurant_id,
    });
    setIsEditing(false);
  };

  const handleCancel = () => {
    setFormData(profile);
    setIsEditing(false);
  };

  const handleLogoClick = () => {
    if (!restaurantId) {
      onError?.('Impossible de changer le logo : aucun restaurant sélectionné.');
      return;
    }
    if (isUploadingLogo) return;
    fileInputRef?.current?.click();
  };

  const handleLogoFileChange = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;

    if (!restaurantId) {
      onError?.('Impossible de téléverser le logo : aucun restaurant sélectionné.');
      if (fileInputRef?.current) fileInputRef.current.value = '';
      return;
    }

    setIsUploadingLogo(true);
    try {
      const publicUrl = await uploadRestaurantLogo(file, restaurantId);
      const updatedData = {
        ...formData,
        logo: publicUrl,
        logoAlt: `Logo du restaurant ${formData?.name || ''}`.trim(),
        restaurantId,
        restaurant_id: restaurantId,
      };
      setFormData(updatedData);
      // Immediately save the new logo to Supabase
      onUpdate(updatedData);
    } catch (err) {
      console.error('Erreur lors du téléchargement du logo:', err);
      onError?.(err?.message || 'Échec du téléversement du logo.');
    } finally {
      setIsUploadingLogo(false);
      // Reset file input so same file can be re-selected
      if (fileInputRef?.current) fileInputRef.current.value = '';
    }
  };

  return (
    <div className="restaurant-profile-card bg-card border border-border rounded-lg p-4 md:p-6 shadow-warm">
      <div className="flex items-center justify-between mb-4 md:mb-6">
        <h3 className="text-base md:text-lg font-heading font-semibold text-foreground">
          Profil du Restaurant
        </h3>
        {!isEditing ? (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsEditing(true)}
            iconName="Edit"
            iconPosition="left"
            iconSize={16}
          >
            Modifier
          </Button>
        ) : (
          <div className="flex items-center gap-2">
            <Button
              variant="default"
              size="sm"
              onClick={handleSave}
              iconName="Check"
              iconPosition="left"
              iconSize={16}
            >
              Enregistrer
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCancel}
              iconName="X"
              iconPosition="left"
              iconSize={16}
            >
              Annuler
            </Button>
          </div>
        )}
      </div>
      <div className="space-y-4 md:space-y-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          {/* Clickable logo */}
          <div
            className={`relative flex-shrink-0 w-20 h-20 md:w-24 md:h-24 rounded-lg overflow-hidden bg-muted group ${restaurantId ? 'cursor-pointer' : 'cursor-not-allowed opacity-80'}`}
            onClick={handleLogoClick}
            title={restaurantId ? 'Cliquer pour changer le logo' : 'Restaurant non sélectionné'}
          >
            <Image
              src={formData?.logo}
              alt={formData?.logoAlt || 'Logo du restaurant'}
              className="w-full h-full object-cover"
            />
            {/* Hover overlay */}
            <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
              {isUploadingLogo ? (
                <Icon name="Loader" size={20} color="white" className="animate-spin" />
              ) : (
                <>
                  <Icon name="Camera" size={20} color="white" />
                  <span className="text-white text-xs mt-1 font-caption text-center px-1">Changer</span>
                </>
              )}
            </div>
          </div>

          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            className="hidden"
            onChange={handleLogoFileChange}
          />

          <div className="flex-1 w-full">
            {isEditing ? (
              <Input
                type="text"
                label="Nom du Restaurant"
                value={formData?.name}
                onChange={(e) => handleChange('name', e?.target?.value)}
              />
            ) : (
              <>
                <h4 className="text-lg md:text-xl font-heading font-bold text-foreground">
                  {formData?.name || 'Restaurant'}
                </h4>
                <p className="text-sm md:text-base font-caption text-muted-foreground">
                  {formData?.tagline}
                </p>
                <p className="text-xs text-muted-foreground mt-1 font-caption italic">
                  Cliquez sur le logo pour le modifier
                </p>
              </>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            {isEditing ? (
              <Input
                type="text"
                label="Slogan"
                value={formData?.tagline}
                onChange={(e) => handleChange('tagline', e?.target?.value)}
              />
            ) : (
              <>
                <p className="text-xs font-caption text-muted-foreground mb-1">Slogan</p>
                <p className="text-sm md:text-base font-body text-foreground">{formData?.tagline}</p>
              </>
            )}
          </div>

          <div>
            {isEditing ? (
              <Input
                type="text"
                label="Téléphone"
                value={formData?.phone}
                onChange={(e) => handleChange('phone', e?.target?.value)}
              />
            ) : (
              <>
                <p className="text-xs font-caption text-muted-foreground mb-1">Téléphone</p>
                <p className="text-sm md:text-base font-data text-foreground">{formData?.phone}</p>
              </>
            )}
          </div>

          <div>
            {isEditing ? (
              <Input
                type="email"
                label="Adresse e-mail"
                value={formData?.email}
                onChange={(e) => handleChange('email', e?.target?.value)}
              />
            ) : (
              <>
                <p className="text-xs font-caption text-muted-foreground mb-1">Adresse e-mail</p>
                <p className="text-sm md:text-base font-data text-foreground">{formData?.email}</p>
              </>
            )}
          </div>

          <div className="md:col-span-2">
            {isEditing ? (
              <Input
                type="text"
                label="Adresse"
                value={formData?.address}
                onChange={(e) => handleChange('address', e?.target?.value)}
              />
            ) : (
              <>
                <p className="text-xs font-caption text-muted-foreground mb-1">Adresse</p>
                <p className="text-sm md:text-base font-body text-foreground">{formData?.address}</p>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RestaurantProfileCard;
